import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MenuService} from '../service/menu.service';
import {Menu} from '../interface/menu';
import {HttpErrorResponse} from '@angular/common/http';
import {MenuItemView} from '../interface/menuItemView';
import {Subscription} from 'rxjs';
import {ImageService} from '../service/image.service';
import {TranslateService} from '@ngx-translate/core';
import {CurrencyService} from '../service/currency.service';
import {SharedConstants} from '../utilityClass/sharedConstants';
import {CurrencyResponse} from '../interface/currencyResponse';

interface MenuItemWithSelection extends MenuItemView {
  selected?: boolean;
  selectedQuantity?: number;
}

@Component({
  selector: 'app-menu-view',
  templateUrl: './menu-view.component.html',
  styleUrl: './menu-view.component.scss'
})
export class MenuViewComponent implements OnInit, OnDestroy {
  menu: Menu | null = null;
  menuItems: MenuItemWithSelection[] = [];
  isLoading: boolean = true;
  error: string | null = null;
  selectedMenuItemIds: Set<string> = new Set();
  allowsMultipleProducts: boolean = false;
  selectedPaymentMethod: 'apple' | 'other' = 'apple';
  qrCode: string = '';
  currencyRate: number = SharedConstants.DOLLAR_EXCHANGE_RATE as unknown as number;
  private routeSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private menuService: MenuService,
    private imageService: ImageService,
    private translateService: TranslateService,
    private currencyService: CurrencyService
  ) {
  }

  goBack(): void {
    if (this.qrCode) {
      this.router.navigate([`/${this.qrCode}`]);
      return;
    }

    this.router.navigate(['/home']);
  }

  ngOnInit() {
    this.qrCode = window.history.state?.qrCode || '';
    this.loadCurrencyRate();
    this.routeSubscription = this.route.params.subscribe(params => {
      const menuIdParam = params['id'];

      if (!menuIdParam) {
        this.error = this.translateService.instant('menu_management.errors.menu_id_not_provided');
        this.isLoading = false;
        return;
      }

      const menuId = Number.parseInt(menuIdParam, 10);
      if (isNaN(menuId) || menuId <= 0) {
        this.error = this.translateService.instant('menu_management.errors.invalid_menu_id');
        this.isLoading = false;
        return;
      }

      this.loadMenu(menuId);
    });
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }

  loadMenu(id: number) {
    this.isLoading = true;
    this.menuService.getMenuById(id).subscribe({
      next: (menu: Menu) => {
        this.menu = menu;
        this.qrCode = this.qrCode || menu.linked_users?.[0] || '';
        this.allowsMultipleProducts = menu.allowsMultipleProducts || false;
        this.menuItems = (menu.menu_items ?? []).map(item => ({
          ...item,
          selected: false,
          selectedQuantity: item.hasQuantity ? 1 : 0
        }));
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.translateService.instant('menu_management.errors.not_found');
        this.isLoading = false;
        if (error.status === 404) {
          this.router.navigate(['/error-page']);
        }
      }
    });
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  getImageSrc(image: string | null): string {
    return this.imageService.resolveImageSrc(image);
  }

  selectMenuItem(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (!item || item.hasQuantity) return;

    if (!this.allowsMultipleProducts) {
      this.selectedMenuItemIds.clear();
    }

    if (this.selectedMenuItemIds.has(itemId)) {
      this.selectedMenuItemIds.delete(itemId);
    } else {
      this.selectedMenuItemIds.add(itemId);
    }
  }

  isMenuItemSelected(itemId: string): boolean {
    return this.selectedMenuItemIds.has(itemId);
  }

  increaseQuantity(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (item && item.hasQuantity) {
      item.selectedQuantity = (item.selectedQuantity || 0) + 1;
      item.selected = item.selectedQuantity > 0;
    }
  }

  decreaseQuantity(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (item && item.hasQuantity && item.selectedQuantity && item.selectedQuantity > 0) {
      item.selectedQuantity -= 1;
      item.selected = item.selectedQuantity > 0;
    }
  }

  get hasNoSelection(): boolean {
    if (this.selectedMenuItemIds.size > 0) return false;
    return !this.menuItems.some(item => item.hasQuantity && (item.selectedQuantity || 0) > 0);
  }

  get totalAmd(): number {
    return this.menuItems.reduce((sum, item) => {
      if (item.hasQuantity) {
        const quantity = item.selectedQuantity || 0;
        return sum + quantity * this.toNumber(item.quantity);
      }
      return this.selectedMenuItemIds.has(item.id) ? sum + this.toNumber(item.price) : sum;
    }, 0);
  }

  get totalUsd(): number {
    return this.toUsd(this.totalAmd);
  }

  toUsd(amd: number | string | undefined): number {
    const value = this.toNumber(amd);
    if (!this.currencyRate) return 0;
    return value / this.currencyRate;
  }

  private loadCurrencyRate(): void {
    this.currencyService.getCurrency(SharedConstants.USD).subscribe({
      next: (data: CurrencyResponse) => {
        const rate = Number(data?.[SharedConstants.USD]);
        if (Number.isFinite(rate) && rate > 0) {
          this.currencyRate = rate;
        }
      }
    });
  }

  setPaymentMethod(method: 'apple' | 'other') {
    if (this.hasNoSelection) return;
    this.selectedPaymentMethod = method;
  }

  openOtherPaymentTemplate() {
    if (this.hasNoSelection || !this.menu) return;
    this.selectedPaymentMethod = 'other';

    const selectedItems = this.menuItems.filter(item => {
      if (item.hasQuantity) {
        return (item.selectedQuantity || 0) > 0;
      }
      return this.selectedMenuItemIds.has(item.id);
    });

    this.router.navigate([`/menu/payment/${this.menu.id}`], {
      state: {
        items: selectedItems,
        total: this.totalAmd,
        menuName: this.menu.menu_name,
        qrCode: this.qrCode
      }
    });
  }

  private toNumber(value: number | string | undefined): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const parsed = Number(value.replace(/[^\d.]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }
}
